import { Button } from "@fieldwork/ui/components/button";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import { ago, errorMessage, Page } from "@/components/ui-bits";
import { client, orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_auth/tokens")({
  component: Tokens,
});

const added = (ts: string | number | Date) => new Date(ts).toLocaleDateString([], { day: "numeric", month: "short" });

function Tokens() {
  const creds = useQuery(orpc.credentials.list.queryOptions());
  const remove = useMutation({
    mutationFn: (id: string) => client.credentials.remove({ id }),
    onSuccess: () => creds.refetch(),
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <Page
      title="Cloud tokens"
      width="max-w-[860px]"
      subtitle={
        <p className="m-0 mt-1 max-w-[70ch] text-sm text-[#CFCBC1]">
          Fieldwork creates servers in your own Hetzner Cloud project, so you pay Hetzner directly. It no longer keeps
          cloud tokens: you paste one when you create, restart or delete a server, and it is used for that action only.
          A Read &amp; Write token can open a server's disk, so it shouldn't sit anywhere you don't control. Remove any
          token saved before this change below, and delete it in the Hetzner console too.
        </p>
      }
    >
      <section aria-labelledby="tk-list" className="rounded-md bg-[#1A1A19] shadow-[inset_0_0_0_1px_#2A2927]">
        <h2 id="tk-list" className="m-0 px-[22px] pt-[18px] pb-2.5 font-mono text-[15px] font-bold">
          Saved earlier
        </h2>
        {creds.error && <p className="m-0 border-t border-[#2A2927] px-[22px] py-3.5 text-[#F48A80]">{creds.error.message}</p>}
        {creds.data?.length === 0 && (
          <p className="m-0 border-t border-[#2A2927] px-[22px] py-3.5 text-[#A8A59D]">None. Nothing to clean up.</p>
        )}
        {creds.data?.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-2.5 border-t border-[#2A2927] px-[22px] py-3.5">
            <span
              aria-hidden="true"
              className="grid size-7 shrink-0 place-items-center rounded-md bg-[#D50C2D] font-mono text-xs font-bold text-white"
            >
              H
            </span>
            <span className="flex min-w-0 flex-[1_1_240px] flex-col">
              <span className="font-medium">{c.label}</span>
              <span className="font-mono text-xs text-[#A8A59D]">
                …{c.hint} · added {added(c.createdAt)} · {c.lastUsedAt ? `used ${ago(c.lastUsedAt)}` : "not used yet"}
              </span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-[#F48A80] hover:text-[#F48A80] hover:bg-[rgba(240,100,90,0.1)]"
              disabled={remove.isPending}
              onClick={() => window.confirm(`Remove the token "${c.label}"?`) && remove.mutate(c.id)}
            >
              Remove
            </Button>
          </div>
        ))}
      </section>

    </Page>
  );
}
